// Catalogue des variables météo journalières que le moteur sait lire. Chaque variable dit d'où
// la synchro la tire (API Open-Meteo, variable horaire) et comment la ramener à un jour local
// (Europe/Paris) : la synchro ne demande que de l'horaire et agrège elle-même.
// Ajouter une variable = une ligne ici + la synchro qui la remplit : l'enum du JSON Schema suit.

export type WeatherSource = 'forecast' | 'marine' | 'air';
export type DailyAggregation = 'sum' | 'mean' | 'min' | 'max';

export interface WeatherVariable {
  label: string;
  unit: string;
  source: WeatherSource;
  /** Variable horaire Open-Meteo d'origine. */
  hourly: string;
  /** Agrégation sur le jour local. */
  daily: DailyAggregation;
}

export const WEATHER_VARIABLES = {
  precipitation_sum: { label: 'Pluie', unit: 'mm', source: 'forecast', hourly: 'precipitation', daily: 'sum' },
  temperature_2m_mean: { label: 'Température moyenne', unit: '°C', source: 'forecast', hourly: 'temperature_2m', daily: 'mean' },
  temperature_2m_min: { label: 'Température minimale', unit: '°C', source: 'forecast', hourly: 'temperature_2m', daily: 'min' },
  temperature_2m_max: { label: 'Température maximale', unit: '°C', source: 'forecast', hourly: 'temperature_2m', daily: 'max' },
  relative_humidity_2m_mean: { label: 'Humidité relative', unit: '%', source: 'forecast', hourly: 'relative_humidity_2m', daily: 'mean' },
  dew_point_2m_mean: { label: 'Point de rosée', unit: '°C', source: 'forecast', hourly: 'dew_point_2m', daily: 'mean' },
  pressure_msl_mean: { label: 'Pression', unit: 'hPa', source: 'forecast', hourly: 'pressure_msl', daily: 'mean' },
  cloud_cover_mean: { label: 'Nébulosité', unit: '%', source: 'forecast', hourly: 'cloud_cover', daily: 'mean' },
  et0_fao_evapotranspiration: { label: 'Évapotranspiration ET0', unit: 'mm', source: 'forecast', hourly: 'et0_fao_evapotranspiration', daily: 'sum' },
  soil_temperature_6cm: { label: 'Température du sol (6 cm)', unit: '°C', source: 'forecast', hourly: 'soil_temperature_6cm', daily: 'mean' },
  soil_moisture_3_to_9cm: { label: 'Humidité du sol (3–9 cm)', unit: 'm³/m³', source: 'forecast', hourly: 'soil_moisture_3_to_9cm', daily: 'mean' },
  wind_speed_10m_max: { label: 'Vent moyen max', unit: 'nds', source: 'forecast', hourly: 'wind_speed_10m', daily: 'max' },
  wind_gusts_10m_max: { label: 'Rafales max', unit: 'nds', source: 'forecast', hourly: 'wind_gusts_10m', daily: 'max' },
  wave_height_max: { label: 'Houle max', unit: 'm', source: 'marine', hourly: 'wave_height', daily: 'max' },
  sea_surface_temperature_mean: { label: 'Température de la mer', unit: '°C', source: 'marine', hourly: 'sea_surface_temperature', daily: 'mean' },
} as const satisfies Record<string, WeatherVariable>;

/** Variables calculées par le moteur à partir des variables de base, jour par jour. */
export const DERIVED_VARIABLES = {
  dew_point_spread: { label: 'Écart température / rosée', unit: '°C', from: ['temperature_2m_mean', 'dew_point_2m_mean'] },
  water_balance: { label: 'Bilan pluie − ET0', unit: 'mm', from: ['precipitation_sum', 'et0_fao_evapotranspiration'] },
} as const satisfies Record<string, { label: string; unit: string; from: readonly (keyof typeof WEATHER_VARIABLES)[] }>;

/**
 * Variables horaires lues telles quelles par les conditions de créneau (kayak, bateau) : la
 * synchro garde leurs valeurs heure par heure. L'id est le nom de la variable horaire Open-Meteo.
 */
export const HOURLY_VARIABLES = {
  wind_speed_10m: { label: 'Vent moyen', unit: 'nds', source: 'forecast' },
  wind_gusts_10m: { label: 'Rafales', unit: 'nds', source: 'forecast' },
  wind_direction_10m: { label: 'Direction du vent (d’où il vient)', unit: '°', source: 'forecast' },
  precipitation: { label: 'Pluie', unit: 'mm', source: 'forecast' },
  wave_height: { label: 'Houle', unit: 'm', source: 'marine' },
  wave_period: { label: 'Période de houle', unit: 's', source: 'marine' },
  /** 1 si l'instant de l'heure est de jour (soleil levé), 0 sinon. */
  is_day: { label: 'Jour', unit: '', source: 'forecast' },
} as const satisfies Record<string, { label: string; unit: string; source: WeatherSource }>;

/**
 * Variables horaires calculées avant le moteur (`withLocation`). Vent de terre = part du vent
 * moyen qui souffle de la terre vers la mer (0 par vent de mer ou parallèle à la côte), propre au
 * lieu ; non calculable sans orientation de la mer. Heure de jour = 1 si l'heure entière est de
 * jour (`is_day` vrai à son début et à son début suivant) : une sortie diurne (D240) ne déborde
 * pas sur la nuit, l'heure du lever ou du coucher est écartée. Crépuscule = écart en heures à
 * l'heure du lever ou du coucher la plus proche (changement d'état d'`is_day`, à ±1 h près).
 */
export const DERIVED_HOURLY_VARIABLES = {
  offshore_wind: { label: 'Vent de terre (vers le large)', unit: 'nds', from: ['wind_speed_10m', 'wind_direction_10m'] },
  daylight: { label: 'Heure entière de jour', unit: '', from: ['is_day'] },
  twilight: { label: 'Écart au lever ou au coucher', unit: 'h', from: ['is_day'] },
} as const satisfies Record<string, { label: string; unit: string; from: readonly (keyof typeof HOURLY_VARIABLES)[] }>;

export type BaseHourlyVariableId = keyof typeof HOURLY_VARIABLES;
export type HourlyVariableId = BaseHourlyVariableId | keyof typeof DERIVED_HOURLY_VARIABLES;
export const HOURLY_VARIABLE_IDS = [...Object.keys(HOURLY_VARIABLES), ...Object.keys(DERIVED_HOURLY_VARIABLES)] as HourlyVariableId[];

/** Variables horaires à demander à la synchro pour ces conditions (dérivées dépliées). */
export function baseHourlyFor(ids: Iterable<HourlyVariableId>): BaseHourlyVariableId[] {
  const needed = new Set<BaseHourlyVariableId>();
  for (const id of ids) {
    if (id in DERIVED_HOURLY_VARIABLES) for (const base of DERIVED_HOURLY_VARIABLES[id as keyof typeof DERIVED_HOURLY_VARIABLES].from) needed.add(base);
    else needed.add(id as BaseHourlyVariableId);
  }
  return [...needed];
}

export type BaseVariableId = keyof typeof WEATHER_VARIABLES;
export type DerivedVariableId = keyof typeof DERIVED_VARIABLES;
export type VariableId = BaseVariableId | DerivedVariableId;

export const VARIABLE_IDS = [...Object.keys(WEATHER_VARIABLES), ...Object.keys(DERIVED_VARIABLES)] as VariableId[];

export function isDerived(id: VariableId): id is DerivedVariableId {
  return id in DERIVED_VARIABLES;
}

/** Variables de base à demander à la synchro pour évaluer ces variables (dérivées dépliées). */
export function baseVariablesFor(ids: Iterable<VariableId>): BaseVariableId[] {
  const needed = new Set<BaseVariableId>();
  for (const id of ids) {
    if (isDerived(id)) for (const base of DERIVED_VARIABLES[id].from) needed.add(base);
    else needed.add(id);
  }
  return [...needed];
}
